import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDSnackbar from "components/MDSnackbar";
import RecipeResultCard from "components/RecipeResultCard";
import SlotSwapPanel from "components/SlotSwapPanel";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import { useCatalogStats, formatCatalogCounts } from "utils/catalogStats";
import { RED, RED_LIGHT, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";
import { buildAvoidOptions, suggestAvoidOptions } from "utils/avoidOptions";
import { buildSchedulePayload } from "layouts/ScheduleBuilder/buildSchedulePayload";
import DietSelector from "components/DietSelector";
import { useDietPreference } from "utils/dietPreference";
import { CATALOG_DEFAULT_SERVINGS } from "utils/servings";
import { MEAL_TYPE_BADGE } from "utils/mealTypes";

// `min` is the lower edge of each bucket and is sent alongside `value` (the
// upper edge) so the backend can treat the selection as a band. "Deep cook"
// is open-ended upward: 120 is a sentinel the backend reads as "at least 60".
const TIME_OPTIONS = [
  { label: "Quick", sub: "15–30 min", value: 30, min: 15 },
  { label: "Regular", sub: "30–45 min", value: 45, min: 30 },
  { label: "Relaxed", sub: "45–60 min", value: 60, min: 45 },
  { label: "Deep cook", sub: "1 hr+", value: 120, min: 60 },
];

// Wording for the relaxation keys the create endpoint returns. Kept in sync
// with RELAXATION_LABELS in back-end/schedule/recommend.py.
const RELAXATION_COPY = {
  cuisine: "leaned on one cuisine more than usual",
  time_floor: "included quicker recipes than you asked for",
  dish_family: "repeated a dish type",
  time_ceiling: "included recipes slightly over your time limit",
  time: "set aside your time preference",
  protein: "couldn't find enough protein-forward recipes",
  meal_type: "couldn't fill every meal type you asked for",
};

// ── Chip input with search dropdown ──
function ChipInput({ placeholder, options, selected, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  // Keyed on `key`, not `id`: the options are ingredients AND ingredient
  // families, and the two live in different id spaces (a group has no id at
  // all). See utils/avoidOptions.js for the shared shape.
  const filtered = suggestAvoidOptions(options, selected, query);

  const add = (item) => {
    onChange([...selected, item]);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const remove = (key) => onChange(selected.filter((s) => s.key !== key));

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          border: "1.5px solid",
          borderColor: BORDER,
          borderRadius: "8px",
          padding: "7px 10px",
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          minHeight: 42,
          cursor: "text",
          bgcolor: "#fff",
          transition: "border-color 0.15s",
          "&:focus-within": { borderColor: RED },
        }}
      >
        {selected.map((s) => (
          <Box
            key={s.key}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              bgcolor: RED_LIGHT,
              color: RED,
              fontSize: 12,
              fontWeight: 600,
              pl: 1.25,
              pr: 0.75,
              py: "3px",
              borderRadius: 20,
            }}
          >
            {s.name}
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                remove(s.key);
              }}
              sx={{
                fontSize: 14,
                opacity: 0.55,
                cursor: "pointer",
                lineHeight: 1,
                "&:hover": { opacity: 1 },
              }}
            >
              ×
            </Box>
          </Box>
        ))}
        <Box
          component="input"
          ref={inputRef}
          value={query}
          placeholder={selected.length === 0 ? placeholder : ""}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          sx={{
            border: "none",
            outline: "none",
            fontSize: 13,
            fontFamily: "Roboto, sans-serif",
            bgcolor: "transparent",
            minWidth: 100,
            flex: 1,
            padding: "2px 4px",
            color: "#222",
            "&::placeholder": { color: MUTED },
          }}
        />
      </Box>

      {open && filtered.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: "4px",
            border: "1.5px solid",
            borderColor: BORDER,
            borderRadius: "8px",
            bgcolor: "#fff",
            boxShadow: "0 4px 14px rgba(0,0,0,0.07)",
            overflow: "hidden",
            zIndex: 50,
          }}
        >
          {filtered.map((item) => (
            <Box
              key={item.key}
              onMouseDown={() => add(item)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                padding: "10px 14px",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": { bgcolor: RED_LIGHT, color: RED },
              }}
            >
              <Box component="span">{item.name}</Box>
              {item.kind === "group" && (
                <Box component="span" sx={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>
                  all {item.memberCount} · group
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

ChipInput.propTypes = {
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, kind: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, name: PropTypes.string }))
    .isRequired,
  onChange: PropTypes.func.isRequired,
};

// The slot types a week is built from — SCHEDULED_MEAL_TYPES on the backend,
// and deliberately not every value in the vocabulary. `side` is absent because
// a side accompanies a main rather than being one, and nothing pairs the two:
// sides are found on Browse and enter a week as a deliberate pick. Asking
// someone how many side slots they want would be asking the wrong question.
const MEAL_TYPES = [
  { key: "main", label: "Mains", emoji: "🍽" },
  { key: "portable", label: "To-go", emoji: "🥪" },
  { key: "snack", label: "Snacks", emoji: "🍎" },
];

function MealTypePopup({ counts, onDone, onClose }) {
  const [local, setLocal] = useState({ ...counts });
  const total = local.main + local.portable + local.snack;

  const MAX_RECIPES = 7;
  const adjust = (key, delta) => {
    setLocal((prev) => {
      const next = Math.max(0, prev[key] + delta);
      const newTotal = prev.main + prev.portable + prev.snack - prev[key] + next;
      if (newTotal > MAX_RECIPES) return prev;
      return { ...prev, [key]: next };
    });
  };

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.35)",
        zIndex: 400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "14px",
          p: "32px 28px",
          width: "100%",
          maxWidth: 380,
          mx: 2,
        }}
      >
        <Box
          sx={{
            fontSize: 16,
            fontWeight: 800,
            color: "#222",
            mb: 0.5,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          Customise meal types
        </Box>
        <Box sx={{ fontSize: 12, color: MUTED, mb: 3, fontFamily: "Roboto, sans-serif" }}>
          Set how many of each type you want. Max 7 total.
        </Box>

        {MEAL_TYPES.map(({ key, label, emoji }) => (
          <Box
            key={key}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}
          >
            <Box
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#333",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {emoji} {label}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                component="button"
                onClick={() => adjust(key, -1)}
                disabled={local[key] === 0}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "1.5px solid",
                  borderColor: local[key] === 0 ? "#e0e0e0" : BORDER,
                  bgcolor: "#fff",
                  color: local[key] === 0 ? "#ccc" : "#444",
                  fontSize: 18,
                  lineHeight: 1,
                  cursor: local[key] === 0 ? "default" : "pointer",
                  fontFamily: "Roboto, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": local[key] > 0 ? { borderColor: RED, color: RED } : {},
                }}
              >
                −
              </Box>
              <Box
                sx={{
                  width: 20,
                  textAlign: "center",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                {local[key]}
              </Box>
              <Box
                component="button"
                onClick={() => adjust(key, 1)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "1.5px solid",
                  borderColor: BORDER,
                  bgcolor: "#fff",
                  color: "#444",
                  fontSize: 18,
                  lineHeight: 1,
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": { borderColor: RED, color: RED },
                }}
              >
                +
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: BORDER,
            pt: 2,
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ fontSize: 13, color: MUTED, fontFamily: "Roboto, sans-serif" }}>Total</Box>
          <Box
            sx={{ fontSize: 14, fontWeight: 700, color: "#222", fontFamily: "Roboto, sans-serif" }}
          >
            {total} recipe{total !== 1 ? "s" : ""}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box
            component="button"
            onClick={() => total > 0 && onDone(local)}
            disabled={total === 0}
            sx={{
              flex: 1,
              padding: "11px",
              bgcolor: total === 0 ? "#ccc" : RED,
              color: "#fff",
              border: "none",
              borderRadius: "9px",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Roboto, sans-serif",
              cursor: total === 0 ? "not-allowed" : "pointer",
              transition: "background 0.15s",
              "&:hover": total > 0 ? { bgcolor: "#7d1520" } : {},
            }}
          >
            Done
          </Box>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              padding: "11px 18px",
              bgcolor: "#f0f0f0",
              color: "#444",
              border: "none",
              borderRadius: "9px",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Roboto, sans-serif",
              cursor: "pointer",
              "&:hover": { bgcolor: "#e4e4e4" },
            }}
          >
            Cancel
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

MealTypePopup.propTypes = {
  counts: PropTypes.shape({
    main: PropTypes.number,
    portable: PropTypes.number,
    snack: PropTypes.number,
  }).isRequired,
  onDone: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

function ScheduleBuilder() {
  const [name, setName] = useState("");
  const [mealCount, setMealCount] = useState(3);
  const [mealTypeCounts, setMealTypeCounts] = useState(null); // null = not customised
  const [typePopupOpen, setTypePopupOpen] = useState(false);
  const [defaultServings, setDefaultServings] = useState(CATALOG_DEFAULT_SERVINGS);
  const [proteinPriority, setProteinPriority] = useState(false);
  // Diet for this build. Seeded from the profile setting and overridable for
  // one week without writing back — cooking one meat dish for a visitor has not
  // made the user stop being vegetarian. `dietTouched` is what makes that
  // possible: until they touch the control we send nothing and let the server
  // read the profile, so a slow preferences response can never build a
  // vegetarian a week of meat by defaulting the control to "no preference".
  const [dietOverride, setDietOverride] = useState(null);
  const { diet: profileDiet, options: dietOptions } = useDietPreference();
  const effectiveDiet = dietOverride ?? profileDiet;
  const [timeValue, setTimeValue] = useState(45);
  const [timeMinValue, setTimeMinValue] = useState(30);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [ingredientGroups, setIngredientGroups] = useState([]);
  // Same option shape the avoid pickers use, minus the avoidable filter's
  // effect on what you can *include*: every group is searchable here, because
  // featuring an ingredient is a bias and can never empty the week.
  const featureOptions = useMemo(
    () =>
      buildAvoidOptions(
        ingredientGroups.map((g) => ({ ...g, avoidable: true })),
        ingredientsList
      ),
    [ingredientGroups, ingredientsList]
  );
  const [pantryItems, setPantryItems] = useState([]);
  const [pantryList, setPantryList] = useState([]);
  const [pantryOpen, setPantryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [reviewRecipes, setReviewRecipes] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, content: "" });
  // Preferences the backend had to loosen to fill this schedule.
  const [relaxations, setRelaxations] = useState([]);
  // What the app learned about this user and actually acted on this week. The
  // backend only sends a note when the taste bias demonstrably changed the
  // schedule, so an empty list is the normal case and must render nothing —
  // never a placeholder implying personalization that didn't happen.
  const [personalizationNotes, setPersonalizationNotes] = useState([]);
  // Slot-scoped replacement picker: which slot is open. Everything inside it —
  // alternatives, search, the swap itself — lives in SlotSwapPanel, shared with
  // the committed schedule view.
  const [pickerSlot, setPickerSlot] = useState(null);
  // Recipes chosen on the browse page. These become locked slots: the backend
  // seeds them into the recommender and fills the rest of the week around
  // them, so they are inputs to the build rather than a bypass of it.
  const [lockedRecipes, setLockedRecipes] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const catalogStats = useCatalogStats();

  useEffect(() => {
    if (!getAuthToken()) navigate("/authentication/sign-in", { replace: true });
  }, [navigate]);

  // Picks arrive through router state rather than the URL: they are a list of
  // whole recipes, and a query string of ids would have to be re-fetched to
  // show a single title.
  useEffect(() => {
    const incoming = location.state?.lockedRecipes;
    if (!incoming || incoming.length === 0) return;
    setLockedRecipes(incoming);
    // A week has to have room for the picks in it. Widening here rather than
    // rejecting at submit means arriving with 4 picks just works.
    setMealCount((current) => Math.max(current, incoming.length));
    // Consume the state so a browser Back/Forward or a refresh doesn't
    // silently re-add picks the user has since removed.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]);

  // Popping the entry pushed when the result screen opened returns to the
  // builder form rather than leaving the page.
  useEffect(() => {
    if (!success) return undefined;
    const handlePopState = () => setSuccess(false);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [success]);

  useEffect(() => {
    Promise.all([
      apiClient.get(ENDPOINTS.pantry.ingredientNameList),
      apiClient.get(ENDPOINTS.pantry.myItems),
      apiClient.get(ENDPOINTS.pantry.pantryNameList),
      apiClient.get(ENDPOINTS.pantry.ingredientGroups),
    ])
      .then(([ingRes, myPantryRes, pantryNameRes, groupRes]) => {
        if (Array.isArray(ingRes.data)) setIngredientsList(ingRes.data);
        if (Array.isArray(myPantryRes.data)) setPantryItems(myPantryRes.data);
        if (Array.isArray(pantryNameRes.data)) setPantryList(pantryNameRes.data);
        if (Array.isArray(groupRes.data)) setIngredientGroups(groupRes.data);
      })
      .catch((e) => console.error(e.message));
  }, []);

  const handleAddPantry = async (item) => {
    try {
      await apiClient.post(ENDPOINTS.pantry.myItems, { pantry_item_id: item.id });
      setPantryItems((prev) => [...prev, item]);
    } catch (err) {
      console.error("Failed to add pantry item:", err.message);
    }
  };

  const handleRemovePantry = async (itemId) => {
    try {
      await apiClient.delete(ENDPOINTS.pantry.removeMyItem(itemId));
      setPantryItems((prev) => prev.filter((p) => p.id !== itemId));
    } catch (err) {
      console.error("Failed to remove pantry item:", err.message);
    }
  };

  const totalMeals = mealTypeCounts
    ? mealTypeCounts.main + mealTypeCounts.portable + mealTypeCounts.snack
    : mealCount;

  const fetchReviewRecipes = async (programId) => {
    setReviewLoading(true);
    try {
      const titlesRes = await apiClient.get(ENDPOINTS.schedule.programTitles(programId));
      const titles = titlesRes.data;
      const detailResults = await Promise.allSettled(
        titles.map((r) => apiClient.get(ENDPOINTS.schedule.recipeDetails(r.id)))
      );
      const usedReasons = new Set();
      setReviewRecipes(
        titles.map((t, i) => {
          const d = detailResults[i].status === "fulfilled" ? detailResults[i].value.data : null;
          const allReasons = t.reasons || [];
          const firstUnused = allReasons.find((r) => !usedReasons.has(r));
          if (firstUnused) usedReasons.add(firstUnused);
          return {
            id: t.id,
            recipe_title: t.recipe_title,
            meal_type: t.meal_type,
            description: d?.description || null,
            time_in_minutes: d?.time_in_minutes || null,
            reasons: firstUnused ? [firstUnused] : [],
          };
        })
      );
    } catch (err) {
      console.error("Failed to load review recipes:", err.message);
    } finally {
      setReviewLoading(false);
    }
  };

  // Swap applied — fold the replacement into the reviewed week in place, so
  // the row keeps its position in the list.
  const handleReplaced = (nr, slot) => {
    setReviewRecipes((prev) =>
      prev.map((r) =>
        r.id === slot.id
          ? {
              id: nr.id,
              recipe_title: nr.recipe_title,
              meal_type: nr.meal_type,
              description: nr.description || null,
              time_in_minutes: nr.time_in_minutes || null,
              reasons: nr.reasons || [],
            }
          : r
      )
    );
  };

  const submit = async () => {
    // The backend rejects this too — it will not quietly drop a pick — but it
    // is a preference the user can fix here, so say so here.
    if (lockedRecipes.length > totalMeals) {
      setSnack({
        open: true,
        content: `You picked ${lockedRecipes.length} recipes but this schedule holds ${totalMeals}. Raise the count or remove a pick.`,
      });
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post(
        ENDPOINTS.schedule.create,
        buildSchedulePayload({
          name,
          totalMeals,
          timeValue,
          timeMinValue,
          ingredients,
          defaultServings,
          proteinPriority,
          mealTypeCounts,
          lockedRecipes,
          dietOverride,
        })
      );
      setRelaxations(res.data.relaxations || []);
      setPersonalizationNotes(res.data.personalization_notes || []);
      const pid = res.data.program_id;
      localStorage.setItem(`estia_servings_default_${pid}`, String(defaultServings));
      setCreatedId(pid);
      setSuccess(true);
      // The result screen replaces the form without a route change, so
      // without its own history entry the browser Back button skips the
      // builder entirely and lands on whatever preceded it (/dashboard).
      window.history.pushState({ estiaBuilderResult: true }, "");
      fetchReviewRecipes(pid);
    } catch (err) {
      console.error("Failed to create schedule:", err.message);
      setSnack({ open: true, content: err.response?.data?.detail || "Failed to create schedule." });
    } finally {
      setLoading(false);
    }
  };

  const label = (text, optional = false) => (
    <Box
      component="label"
      sx={{ fontSize: 13, fontWeight: 600, color: "#444", display: "block", mb: 1.25 }}
    >
      {text}{" "}
      {optional && (
        <Box component="span" sx={{ fontWeight: 400, color: MUTED }}>
          (optional)
        </Box>
      )}
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6, px: { xs: 0, md: 3 }, display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            bgcolor: "#fff",
            borderRadius: { xs: "10px", md: "14px" },
            border: "1.5px solid",
            borderColor: BORDER,
            p: { xs: "20px 16px", md: "36px 40px" },
          }}
        >
          {/* Title */}
          <Box
            sx={{
              fontSize: 20,
              fontWeight: 800,
              color: "#222",
              mb: 0.5,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Build a schedule
          </Box>
          <Box sx={{ fontSize: 13, color: MUTED, mb: 1.5, fontFamily: "Roboto, sans-serif" }}>
            We&apos;ll find the right recipes for you.
          </Box>

          {/* What there is to choose from. Real counts, so the promise above
              is backed by a number the user can go and check. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              mb: 4,
              px: 1.5,
              py: 1,
              borderRadius: "8px",
              bgcolor: RED_LIGHT,
            }}
          >
            <Box
              sx={{ fontSize: 12.5, fontWeight: 600, color: RED, fontFamily: "Roboto, sans-serif" }}
            >
              {formatCatalogCounts(catalogStats) || "Loading the catalogue…"}
            </Box>
            <Box
              component="span"
              onClick={() => navigate("/browse")}
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: RED,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Browse them →
            </Box>
          </Box>

          {/* Picks brought over from the browse page. Named "locked" because
              that is what they are to the builder: it plans the rest of the
              week around them rather than choosing them again. */}
          {lockedRecipes.length > 0 && (
            <Box sx={{ mb: 3.5 }}>
              {label(`Your picks (${lockedRecipes.length})`)}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {lockedRecipes.map((r) => (
                  <RecipeResultCard
                    key={r.id}
                    title={r.recipe_title}
                    meta={[
                      r.cuisine,
                      r.time_in_minutes ? `${r.time_in_minutes} min` : null,
                      "locked into this schedule",
                    ]}
                    selectable
                    selected
                    onToggleSelect={() =>
                      setLockedRecipes((prev) => prev.filter((p) => p.id !== r.id))
                    }
                  />
                ))}
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  color: MUTED,
                  mt: 1,
                  fontFamily: "Roboto, sans-serif",
                  lineHeight: 1.5,
                }}
              >
                We&apos;ll keep these and fill the remaining{" "}
                {Math.max(0, totalMeals - lockedRecipes.length)} slot
                {totalMeals - lockedRecipes.length === 1 ? "" : "s"} around them.
              </Box>
            </Box>
          )}

          {/* Name */}
          <Box sx={{ mb: 3.5 }}>
            {label("Schedule name")}
            <Box
              component="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Healthy week, Post-holiday reset…"
              sx={{
                width: "100%",
                padding: "10px 14px",
                border: "1.5px solid",
                borderColor: BORDER,
                borderRadius: "8px",
                fontSize: 14,
                fontFamily: "Roboto, sans-serif",
                outline: "none",
                color: "#222",
                "&:focus": { borderColor: RED },
                "&::placeholder": { color: MUTED },
                transition: "border-color 0.15s",
              }}
            />
          </Box>

          {/* Recipe count */}
          <Box sx={{ mb: 3.5 }}>
            {label("Number of recipes")}
            {mealTypeCounts ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1.5px solid",
                  borderColor: BORDER,
                  borderRadius: "10px",
                  px: 2,
                  py: 1.25,
                  bgcolor: "#fafafa",
                }}
              >
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#333",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {[
                    mealTypeCounts.main > 0 &&
                      `🍽 ${mealTypeCounts.main} main${mealTypeCounts.main !== 1 ? "s" : ""}`,
                    mealTypeCounts.portable > 0 && `🥪 ${mealTypeCounts.portable} to-go`,
                    mealTypeCounts.snack > 0 &&
                      `🍎 ${mealTypeCounts.snack} snack${mealTypeCounts.snack !== 1 ? "s" : ""}`,
                  ]
                    .filter(Boolean)
                    .join("  ·  ")}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    component="span"
                    onClick={() => setTypePopupOpen(true)}
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: RED,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Edit
                  </Box>
                  <Box
                    component="span"
                    onClick={() => setMealTypeCounts(null)}
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: MUTED,
                      cursor: "pointer",
                      fontFamily: "Roboto, sans-serif",
                      "&:hover": { color: "#444" },
                    }}
                  >
                    Clear
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {/* The pick count is added as an option when it exceeds the
                      usual range, so arriving with 7 picks still has a size
                      that fits them. Anything smaller is disabled rather than
                      hidden — the reason it can't be chosen is visible. */}
                  {[...new Set([1, 2, 3, 4, 5, lockedRecipes.length].filter((n) => n >= 1))]
                    .sort((a, b) => a - b)
                    .map((n) => {
                      const tooSmall = n < lockedRecipes.length;
                      return (
                        <Box
                          key={n}
                          onClick={() => !tooSmall && setMealCount(n)}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "10px",
                            border: "1.5px solid",
                            borderColor: mealCount === n ? RED : BORDER,
                            bgcolor: mealCount === n ? RED : "#fff",
                            // eslint-disable-next-line no-nested-ternary
                            color: mealCount === n ? "#fff" : tooSmall ? MUTED : "#777",
                            opacity: tooSmall ? 0.55 : 1,
                            fontSize: 15,
                            fontWeight: 700,
                            fontFamily: "Roboto, sans-serif",
                            cursor: tooSmall ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.12s",
                            "&:hover":
                              mealCount !== n && !tooSmall ? { borderColor: RED, color: RED } : {},
                            userSelect: "none",
                          }}
                        >
                          {n}
                        </Box>
                      );
                    })}
                </Box>
                <Box
                  component="span"
                  onClick={() => setTypePopupOpen(true)}
                  sx={{
                    display: "inline-block",
                    mt: 1.25,
                    fontSize: 12,
                    fontWeight: 600,
                    color: MUTED,
                    cursor: "pointer",
                    fontFamily: "Roboto, sans-serif",
                    "&:hover": { color: RED },
                  }}
                >
                  + Customise by type
                </Box>
              </>
            )}
          </Box>

          {/* Time */}
          <Box sx={{ mb: 3.5 }}>
            {label("Time per recipe")}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
              {TIME_OPTIONS.map((opt) => (
                <Box
                  key={opt.value}
                  onClick={() => {
                    setTimeValue(opt.value);
                    setTimeMinValue(opt.min);
                  }}
                  sx={{
                    border: "1.5px solid",
                    borderColor: timeValue === opt.value ? RED : BORDER,
                    bgcolor: timeValue === opt.value ? RED_LIGHT : "#fff",
                    borderRadius: "10px",
                    padding: "12px 10px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.12s",
                    "&:hover": timeValue !== opt.value ? { borderColor: RED } : {},
                    userSelect: "none",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: timeValue === opt.value ? RED : "#222",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    {opt.label}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11,
                      color: timeValue === opt.value ? "#c45060" : MUTED,
                      mt: "2px",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    {opt.sub}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Default servings */}
          <Box sx={{ mb: 3.5 }}>
            {label("Servings per recipe")}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                component="button"
                onClick={() => setDefaultServings((v) => Math.max(1, v - 1))}
                disabled={defaultServings <= 1}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "9px",
                  border: "1.5px solid",
                  borderColor: defaultServings <= 1 ? "#e0e0e0" : BORDER,
                  bgcolor: "#fff",
                  color: defaultServings <= 1 ? "#ccc" : "#444",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: defaultServings <= 1 ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "Roboto, sans-serif",
                  "&:hover": defaultServings > 1 ? { borderColor: RED, color: RED } : {},
                }}
              >
                −
              </Box>
              <Box
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                {defaultServings} {defaultServings === 1 ? "serving" : "servings"}
              </Box>
              <Box
                component="button"
                onClick={() => setDefaultServings((v) => Math.min(12, v + 1))}
                disabled={defaultServings >= 12}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "9px",
                  border: "1.5px solid",
                  borderColor: defaultServings >= 12 ? "#e0e0e0" : BORDER,
                  bgcolor: "#fff",
                  color: defaultServings >= 12 ? "#ccc" : "#444",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: defaultServings >= 12 ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "Roboto, sans-serif",
                  "&:hover": defaultServings < 12 ? { borderColor: RED, color: RED } : {},
                }}
              >
                +
              </Box>
            </Box>
          </Box>

          {/* Diet. Defaults to the profile setting; changing it here applies to
              this week only and never writes back to the profile. */}
          <Box sx={{ mb: 3.5 }}>
            {label("Diet")}
            <DietSelector
              value={effectiveDiet}
              options={dietOptions}
              onChange={setDietOverride}
              note={
                dietOverride && dietOverride !== profileDiet
                  ? "This week only — your profile setting is unchanged."
                  : undefined
              }
            />
          </Box>

          {/* Prioritize protein */}
          <Box sx={{ mb: 3.5 }}>
            <Box
              onClick={() => setProteinPriority((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                border: "1.5px solid",
                borderColor: proteinPriority ? RED : BORDER,
                borderRadius: "10px",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
              data-testid="protein-priority-toggle"
            >
              <Box>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#333",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  🍗 Prioritize protein
                </Box>
                <Box
                  sx={{ fontSize: 11, color: MUTED, fontFamily: "Roboto, sans-serif", mt: "2px" }}
                >
                  Make sure most of the week has a protein-forward main, not just sides.
                </Box>
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 22,
                  borderRadius: "11px",
                  bgcolor: proteinPriority ? RED : "#ddd",
                  position: "relative",
                  transition: "background-color 0.15s",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: proteinPriority ? 20 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    transition: "left 0.15s",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Divider */}
          <Box sx={{ borderTop: "1px solid", borderColor: BORDER, my: 3.5 }} />

          {/* Ingredients */}
          <Box sx={{ mb: 3.5 }}>
            {label("Feature ingredients", true)}
            <ChipInput
              placeholder="Search — try “fish” or “tomato”…"
              options={featureOptions}
              selected={ingredients}
              onChange={setIngredients}
            />
          </Box>

          {/* Pantry banner */}
          <Box
            onClick={() => setPantryOpen(true)}
            sx={{
              mt: 3.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              bgcolor: "#fffbea",
              border: "1.5px solid #f0d060",
              borderRadius: "10px",
              px: 2,
              py: 1.5,
              cursor: "pointer",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "#fff8d6" },
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#7a5c00",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                🧂 {pantryItems.length} pantry item{pantryItems.length !== 1 ? "s" : ""} saved
              </Box>
              <Box
                sx={{ fontSize: 11, color: "#a07820", fontFamily: "Roboto, sans-serif", mt: "2px" }}
              >
                We&apos;ll use these when building your grocery list. Tap to update.
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#7a5c00",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              Update →
            </Box>
          </Box>

          {/* Submit */}
          <Box
            component="button"
            onClick={submit}
            disabled={loading}
            sx={{
              width: "100%",
              mt: 4,
              padding: "13px",
              bgcolor: loading ? "#ccc" : RED,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "Roboto, sans-serif",
              cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: "0.2px",
              transition: "background 0.15s",
              "&:hover": loading ? {} : { bgcolor: "#7d1520" },
            }}
          >
            {loading ? "Building…" : "Build my schedule"}
          </Box>
        </Box>
      </Box>

      {/* Review overlay */}
      {success && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            pt: "68px",
            pb: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              width: "100%",
              maxWidth: 460,
              maxHeight: "calc(100vh - 84px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Header */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Box
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                  mb: 0.5,
                }}
              >
                Your schedule is ready
              </Box>
              <Box sx={{ fontSize: 13, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
                Here&apos;s what we picked. Swap anything before you start.
              </Box>
              {/* Honest note when the catalog couldn't satisfy a preference.
                  Without this the schedule just looks wrong rather than
                  explained. */}
              {relaxations.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: "#fff8e1",
                    border: "1px solid #ffe0a3",
                    borderRadius: "8px",
                    fontSize: 12,
                    color: "#7a5b12",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.45,
                  }}
                >
                  To fill this week we{" "}
                  {relaxations
                    .map((key) => RELAXATION_COPY[key])
                    .filter(Boolean)
                    .join(", ")}
                  .
                </Box>
              )}
              {/* What we learned from this user's own behaviour and acted on.
                  Distinct from the amber relaxation note above: that one
                  apologises for a compromise, this one explains a deliberate
                  choice. Only ever populated when the schedule genuinely
                  differs from the one we'd have built without the model. */}
              {personalizationNotes.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    px: 1.5,
                    py: 1.25,
                    bgcolor: GREEN_LIGHT,
                    border: "1px solid #c7e6c9",
                    borderRadius: "8px",
                    fontSize: 12,
                    color: "#2f6b33",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.45,
                  }}
                >
                  {personalizationNotes.map((note) => (
                    <Box key={`${note.dimension}:${note.key}`} sx={{ "& + &": { mt: 0.75 } }}>
                      {note.text}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ borderTop: "1px solid", borderColor: BORDER }} />

            {/* Recipe list */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {reviewLoading ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    fontSize: 13,
                    color: MUTED,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  Loading…
                </Box>
              ) : (
                reviewRecipes.map((recipe, i) => {
                  const badge = MEAL_TYPE_BADGE[recipe.meal_type] || {
                    label: recipe.meal_type || "",
                    color: MUTED,
                    bg: "#f5f5f5",
                  };
                  return (
                    <Box
                      key={recipe.id}
                      sx={{
                        px: 3,
                        py: 1.75,
                        borderBottom: i < reviewRecipes.length - 1 ? "1px solid" : "none",
                        borderColor: BORDER,
                      }}
                    >
                      {/* Top row: badge + swap button */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.6,
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: badge.color,
                            bgcolor: badge.bg,
                            px: 1.25,
                            py: "3px",
                            borderRadius: 20,
                            fontFamily: "Roboto, sans-serif",
                            letterSpacing: "0.2px",
                          }}
                        >
                          {badge.label}
                        </Box>
                        <Box
                          component="button"
                          onClick={() => setPickerSlot(recipe)}
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#fff",
                            bgcolor: RED,
                            border: "none",
                            borderRadius: "7px",
                            px: "12px",
                            py: "5px",
                            cursor: "pointer",
                            fontFamily: "Roboto, sans-serif",
                            flexShrink: 0,
                            transition: "background 0.15s",
                            "&:hover": { bgcolor: "#7d1520" },
                          }}
                        >
                          ↺ Replace
                        </Box>
                      </Box>
                      {/* Title */}
                      <Box
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#222",
                          fontFamily: "Roboto, sans-serif",
                          mb: 0.4,
                        }}
                      >
                        {recipe.recipe_title}
                      </Box>
                      {/* Why this recipe chip — falls back to description for recipes without reasons */}
                      {recipe.reasons && recipe.reasons.length > 0 ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            bgcolor: "#ede9fe",
                            color: "#5b21b6",
                            borderRadius: "999px",
                            px: 1,
                            py: 0.3,
                            mb: recipe.time_in_minutes ? 0.6 : 0,
                            maxWidth: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <Box component="span" sx={{ fontSize: 11, flexShrink: 0 }}>
                            ✨
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 12,
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {recipe.reasons[0]}
                          </Box>
                        </Box>
                      ) : recipe.description ? (
                        <Box
                          sx={{
                            fontSize: 12,
                            color: MUTED,
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: recipe.time_in_minutes ? 0.6 : 0,
                          }}
                        >
                          {recipe.description}
                        </Box>
                      ) : null}
                      {/* Time */}
                      {recipe.time_in_minutes && (
                        <Box sx={{ fontSize: 13, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
                          ⏱ {recipe.time_in_minutes} min
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>

            <Box sx={{ borderTop: "1px solid", borderColor: BORDER }} />

            {/* Footer */}
            <Box sx={{ px: 3, py: 2, display: "flex", gap: 1 }}>
              <Box
                component="button"
                onClick={() =>
                  navigate(`/module/${createdId}`, {
                    replace: true,
                    state: { from: "Schedule Builder" },
                  })
                }
                sx={{
                  flex: 1,
                  padding: "9px",
                  bgcolor: RED,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: "#7d1520" },
                }}
              >
                Let&apos;s go →
              </Box>
              <Box
                component="button"
                onClick={() => navigate("/dashboard")}
                sx={{
                  padding: "9px 16px",
                  bgcolor: "#f0f0f0",
                  color: "#555",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  "&:hover": { bgcolor: "#e4e4e4" },
                }}
              >
                Dashboard
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Slot picker — bounded alternatives for one slot, plus a capped
          search within it. Deliberately scoped to the slot rather than
          offering a browse of the whole catalog. Shared with the committed
          schedule view: one swap panel, one set of suggestions. */}
      {pickerSlot && (
        <SlotSwapPanel
          programId={createdId}
          slot={pickerSlot}
          onReplaced={handleReplaced}
          onError={(content) => setSnack({ open: true, content })}
          onClose={() => setPickerSlot(null)}
        />
      )}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        dateTime=""
        content={snack.content}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />

      {/* Meal type popup */}
      {typePopupOpen && (
        <MealTypePopup
          counts={mealTypeCounts || { main: mealCount, portable: 0, snack: 0 }}
          onDone={(counts) => {
            setMealTypeCounts(counts);
            setTypePopupOpen(false);
          }}
          onClose={() => setTypePopupOpen(false)}
        />
      )}

      {/* Pantry popup */}
      {pantryOpen && (
        <Box
          onClick={() => setPantryOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.35)",
            zIndex: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              p: "32px 28px",
              width: "100%",
              maxWidth: 480,
              mx: 2,
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                🧂 Your cupboard
              </Box>
              <Box
                component="span"
                onClick={() => setPantryOpen(false)}
                sx={{
                  fontSize: 18,
                  color: MUTED,
                  cursor: "pointer",
                  lineHeight: 1,
                  "&:hover": { color: "#222" },
                }}
              >
                ×
              </Box>
            </Box>
            <Box sx={{ fontSize: 12, color: MUTED, mb: 2.5, fontFamily: "Roboto, sans-serif" }}>
              Items you always have at home. We&apos;ll leave these off your grocery list.
            </Box>

            {/* Current items */}
            <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
              {pantryItems.length === 0 ? (
                <Box sx={{ fontSize: 13, color: MUTED, fontFamily: "Roboto, sans-serif", py: 1 }}>
                  No cupboard items yet.
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {pantryItems.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        bgcolor: "#fffbea",
                        border: "1.5px solid #f0d060",
                        color: "#7a5c00",
                        fontSize: 12,
                        fontWeight: 500,
                        pl: 1.25,
                        pr: 0.75,
                        py: "4px",
                        borderRadius: 20,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {item.name}
                      <Box
                        component="span"
                        onClick={() => handleRemovePantry(item.id)}
                        sx={{
                          fontSize: 14,
                          opacity: 0.5,
                          cursor: "pointer",
                          lineHeight: 1,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Add input */}
            <Box sx={{ borderTop: "1px solid", borderColor: BORDER, pt: 2 }}>
              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#444",
                  mb: 1,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Add item
              </Box>
              <ChipInput
                placeholder="Search cupboard items…"
                options={pantryList.filter((p) => !pantryItems.find((pi) => pi.id === p.id))}
                selected={[]}
                onChange={(items) => {
                  if (items.length > 0) handleAddPantry(items[items.length - 1]);
                }}
              />
            </Box>

            {/* Done button */}
            <Box
              component="button"
              onClick={() => setPantryOpen(false)}
              sx={{
                mt: 2.5,
                width: "100%",
                padding: "11px",
                bgcolor: RED,
                color: "#fff",
                border: "none",
                borderRadius: "9px",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "Roboto, sans-serif",
                cursor: "pointer",
                transition: "background 0.15s",
                "&:hover": { bgcolor: "#7d1520" },
              }}
            >
              Done
            </Box>
          </Box>
        </Box>
      )}
    </DashboardLayout>
  );
}

export default ScheduleBuilder;
